import { useCallback, useMemo, useState } from 'react';
import {
  DiscountConfig,
  ExchangeFormData,
  ExchangeSummary,
  MarkupConfig,
  SelectedProduct,
} from '../../../../types/sales.types';
import { roundMoney } from '../../../../utils/money';
import {
  clampUniformDiscountValue,
  getMaxUniformDiscountAmount,
  hasPositiveCatalogSubtotal,
  isLineDiscountInvalid,
  isUniformAmountDiscountInvalid,
  projectUniformAdjustedPrices,
} from '../../../../utils/sales/line-pricing.utils';
import {
  clampExchangeUniformDiscountValue,
  getExchangeMinFinalPriceForProduct,
  getMaxDiscountAmountForExchange,
  getMaxDiscountPercentageForExchange,
  getMaxUniformDiscountAmountForExchange,
  getMaxUniformDiscountPercentageForExchange,
  violatesExchangeCredit,
} from './exchangePricing.utils';

/** Exchange step 2 sub-flow: 1=commercial config, 2=products, 3=payments */
export type ExchangeStep2FlowPhase = 1 | 2 | 3 | 4;

interface PricingConfigDraft {
  markupEnabled: boolean;
  allowIndividualMarkups: boolean;
  markupType: 'PERCENTAGE' | 'AMOUNT';
  markupValue: number;
  discountEnabled: boolean;
  allowIndividualDiscounts: boolean;
  discountType: 'PERCENTAGE' | 'AMOUNT';
  discountValue: number;
}

const INITIAL_PRICING_DRAFT: PricingConfigDraft = {
  markupEnabled: false,
  allowIndividualMarkups: false,
  markupType: 'AMOUNT',
  markupValue: 0,
  discountEnabled: false,
  allowIndividualDiscounts: false,
  discountType: 'AMOUNT',
  discountValue: 0,
};

function getProjectedAdjustedPrices(
  products: Array<{ originalPrice: number }>,
  draft: PricingConfigDraft,
): Array<{ adjustedPrice: number }> {
  if (!draft.markupEnabled || draft.allowIndividualMarkups) {
    return products.map((product) => ({ adjustedPrice: roundMoney(product.originalPrice) }));
  }

  return projectUniformAdjustedPrices(products, {
    markupType: draft.markupType,
    markupValue: draft.markupValue,
  });
}

export interface UseExchangeStep2FlowProps {
  exchangeData: ExchangeFormData;
  exchangeSummary: ExchangeSummary;
  paymentFlowPhase: ExchangeStep2FlowPhase;
  setPaymentFlowPhase: (phase: ExchangeStep2FlowPhase) => void;
  paymentsLocked: boolean;
  setPaymentsLocked: (locked: boolean) => void;
  onClearPayments: () => void;
  resetAllLinePricing: () => void;
  commitCommercialPricingConfig: (draft: PricingConfigDraft) => void;
  updateMarkupConfig: (config: Partial<MarkupConfig>) => void;
  updateDiscountConfig: (config: Partial<DiscountConfig>) => void;
  applyUniformMarkupToNewProducts: (
    markupType: 'PERCENTAGE' | 'AMOUNT',
    markupValue: number,
  ) => void;
  applyUniformDiscountToNewProducts: (
    discountType: 'PERCENTAGE' | 'AMOUNT',
    discountValue: number,
  ) => void;
}

export const useExchangeStep2Flow = ({
  exchangeData,
  exchangeSummary,
  paymentFlowPhase,
  setPaymentFlowPhase,
  paymentsLocked,
  setPaymentsLocked,
  onClearPayments,
  resetAllLinePricing,
  commitCommercialPricingConfig,
  updateMarkupConfig: _updateMarkupConfig,
  updateDiscountConfig: _updateDiscountConfig,
  applyUniformMarkupToNewProducts: _applyUniformMarkupToNewProducts,
  applyUniformDiscountToNewProducts: _applyUniformDiscountToNewProducts,
}: UseExchangeStep2FlowProps) => {
  const [pricingDraft, setPricingDraft] = useState<PricingConfigDraft>(INITIAL_PRICING_DRAFT);
  const exchangeCredit = exchangeSummary.paidForExchangedItems;
  const flowPhase = paymentFlowPhase;

  const updatePricingDraft = useCallback((partial: Partial<PricingConfigDraft>) => {
    setPricingDraft((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetPricingDraft = useCallback(() => {
    setPricingDraft(INITIAL_PRICING_DRAFT);
  }, []);

  const projectedAdjustedProducts = useMemo(
    () => getProjectedAdjustedPrices(exchangeData.newProducts, pricingDraft),
    [pricingDraft, exchangeData.newProducts],
  );

  const maxUniformDiscountAmount = useMemo(() => {
    if (flowPhase === 1) {
      const projectedMax = getMaxUniformDiscountAmount(projectedAdjustedProducts);
      if (exchangeCredit <= 0) return projectedMax;

      const adjustedTotal = roundMoney(
        projectedAdjustedProducts.reduce((sum, product) => sum + product.adjustedPrice, 0),
      );
      const maxByCredit = roundMoney(Math.max(0, adjustedTotal - exchangeCredit));
      return Math.min(projectedMax, maxByCredit);
    }

    return getMaxUniformDiscountAmountForExchange(exchangeData.newProducts, exchangeCredit);
  }, [flowPhase, projectedAdjustedProducts, exchangeData.newProducts, exchangeCredit]);

  const orderMarkupHasNoCatalogBase = useMemo(
    () =>
      pricingDraft.markupEnabled &&
      !pricingDraft.allowIndividualMarkups &&
      !hasPositiveCatalogSubtotal(exchangeData.newProducts),
    [exchangeData.newProducts, pricingDraft.allowIndividualMarkups, pricingDraft.markupEnabled],
  );

  const violatesCredit = useMemo(
    () =>
      exchangeData.newProducts.length > 0 &&
      violatesExchangeCredit(exchangeSummary.newItemsTotal, exchangeCredit),
    [exchangeData.newProducts.length, exchangeSummary.newItemsTotal, exchangeCredit],
  );

  const summaryIncoherent = !exchangeSummary.isSummaryCoherent;
  const exchangeSummaryInvalid = violatesCredit || summaryIncoherent;

  const productMinFinalPrices = useMemo(() => {
    const map = new Map<number, number>();
    for (const product of exchangeData.newProducts) {
      map.set(
        product.warehouseInventoryProduct.id,
        getExchangeMinFinalPriceForProduct(
          product.warehouseInventoryProduct.id,
          exchangeData.newProducts,
          true,
          exchangeCredit,
        ),
      );
    }
    return map;
  }, [exchangeData.newProducts, exchangeCredit]);

  const hasInvalidLinePricing = useMemo(() => {
    const hasCommercial =
      (exchangeData.markupConfig?.enabled ?? false) ||
      (exchangeData.discountConfig?.enabled ?? false);
    if (flowPhase < 2 || !hasCommercial) {
      return false;
    }

    const invalidMarkup = exchangeData.newProducts.some(
      (product) => product.adjustedPrice < product.originalPrice,
    );
    if (invalidMarkup) {
      return true;
    }

    if (!exchangeData.discountConfig?.enabled) {
      return exchangeSummaryInvalid;
    }

    const discountConfig = exchangeData.discountConfig;
    if (!discountConfig.allowIndividualDiscounts && discountConfig.orderDiscountType === 'AMOUNT') {
      return (
        isUniformAmountDiscountInvalid(
          exchangeData.newProducts.map((product) => ({
            adjustedPrice: product.adjustedPrice,
          })),
          discountConfig.orderDiscountValue,
        ) || exchangeSummaryInvalid
      );
    }

    const hasStructuralDiscountError = exchangeData.newProducts.some((product) => {
      if (product.discountType === 'NONE' || !product.discountValue) {
        return false;
      }

      const adjustedPrice = roundMoney(product.adjustedPrice);
      const minFinal =
        productMinFinalPrices.get(product.warehouseInventoryProduct.id) ??
        getExchangeMinFinalPriceForProduct(
          product.warehouseInventoryProduct.id,
          exchangeData.newProducts,
          true,
          exchangeCredit,
        );

      if (isLineDiscountInvalid(adjustedPrice, product.discountType, product.discountValue)) {
        return true;
      }

      if (product.discountType === 'PERCENTAGE') {
        const maxPct = getMaxDiscountPercentageForExchange(adjustedPrice, minFinal);
        return product.discountValue > maxPct;
      }

      if (product.discountType === 'AMOUNT') {
        const maxAmount = getMaxDiscountAmountForExchange(adjustedPrice, minFinal);
        return product.discountValue > maxAmount;
      }

      return false;
    });

    return hasStructuralDiscountError || exchangeSummaryInvalid;
  }, [
    flowPhase,
    exchangeData.discountConfig,
    exchangeData.newProducts,
    exchangeCredit,
    exchangeSummaryInvalid,
    productMinFinalPrices,
  ]);

  const canConfirmConfigSection = useMemo(() => {
    if (exchangeData.newProducts.length === 0) {
      return false;
    }

    if (!pricingDraft.discountEnabled || pricingDraft.allowIndividualDiscounts) {
      return true;
    }

    if (pricingDraft.discountType === 'PERCENTAGE') {
      return pricingDraft.discountValue <= 99;
    }

    return !isUniformAmountDiscountInvalid(
      flowPhase === 1 ? projectedAdjustedProducts : exchangeData.newProducts,
      pricingDraft.discountValue,
    );
  }, [flowPhase, pricingDraft, projectedAdjustedProducts, exchangeData.newProducts]);

  const canConfirmProductsSection = useMemo(() => {
    return exchangeData.newProducts.length > 0 && !hasInvalidLinePricing;
  }, [exchangeData.newProducts.length, hasInvalidLinePricing]);

  const hasCommercialConfig = useMemo(
    () =>
      flowPhase === 1
        ? pricingDraft.markupEnabled || pricingDraft.discountEnabled
        : (exchangeData.markupConfig?.enabled ?? false) ||
          (exchangeData.discountConfig?.enabled ?? false),
    [
      exchangeData.discountConfig?.enabled,
      exchangeData.markupConfig?.enabled,
      flowPhase,
      pricingDraft.discountEnabled,
      pricingDraft.markupEnabled,
    ],
  );

  const commitPricingConfig = useCallback(() => {
    if (!canConfirmConfigSection) return;
    commitCommercialPricingConfig(pricingDraft);
    onClearPayments();
    setPaymentsLocked(false);
    setPaymentFlowPhase(pricingDraft.markupEnabled || pricingDraft.discountEnabled ? 2 : 3);
  }, [
    canConfirmConfigSection,
    commitCommercialPricingConfig,
    onClearPayments,
    pricingDraft,
    setPaymentFlowPhase,
    setPaymentsLocked,
  ]);

  const confirmProductsSection = useCallback(() => {
    if (!canConfirmProductsSection) return;
    onClearPayments();
    setPaymentsLocked(false);
    setPaymentFlowPhase(3);
  }, [canConfirmProductsSection, onClearPayments, setPaymentFlowPhase, setPaymentsLocked]);

  const confirmPaymentConfigSection = useCallback(() => {
    onClearPayments();
    setPaymentsLocked(false);
    setPaymentFlowPhase(4);
  }, [onClearPayments, setPaymentFlowPhase, setPaymentsLocked]);

  const unlockConfigForEdit = useCallback(() => {
    resetAllLinePricing();
    resetPricingDraft();
    onClearPayments();
    setPaymentsLocked(false);
    setPaymentFlowPhase(1);
  }, [
    onClearPayments,
    resetAllLinePricing,
    resetPricingDraft,
    setPaymentFlowPhase,
    setPaymentsLocked,
  ]);

  const unlockProductsForEdit = useCallback(() => {
    onClearPayments();
    setPaymentsLocked(false);
    setPaymentFlowPhase(2);
  }, [onClearPayments, setPaymentFlowPhase, setPaymentsLocked]);

  const unlockPaymentConfigForEdit = useCallback(() => {
    onClearPayments();
    setPaymentsLocked(false);
    setPaymentFlowPhase(3);
  }, [onClearPayments, setPaymentFlowPhase, setPaymentsLocked]);

  const unlockPaymentMethodsForEdit = useCallback(() => {
    setPaymentsLocked(false);
  }, [setPaymentsLocked]);

  const handleMarkupToggle = useCallback(
    (enabled: boolean) => {
      if (!enabled) {
        updatePricingDraft({
          markupEnabled: false,
          allowIndividualMarkups: false,
          markupType: 'AMOUNT',
          markupValue: 0,
        });
        return;
      }
      updatePricingDraft({ markupEnabled: true });
    },
    [updatePricingDraft],
  );

  const handleIndividualMarkupToggle = useCallback(
    (enabled: boolean) => {
      updatePricingDraft({ allowIndividualMarkups: enabled });
    },
    [updatePricingDraft],
  );

  const handleMarkupTypeChange = useCallback(
    (type: 'PERCENTAGE' | 'AMOUNT') => {
      updatePricingDraft({ markupType: type, markupValue: 0 });
    },
    [updatePricingDraft],
  );

  const handleMarkupValueChange = useCallback(
    (value: number) => {
      updatePricingDraft({ markupValue: roundMoney(Math.max(0, value)) });
    },
    [updatePricingDraft],
  );

  const handleDiscountToggle = useCallback(
    (enabled: boolean) => {
      if (!enabled) {
        updatePricingDraft({
          discountEnabled: false,
          allowIndividualDiscounts: false,
          discountType: 'AMOUNT',
          discountValue: 0,
        });
        return;
      }
      updatePricingDraft({ discountEnabled: true });
    },
    [updatePricingDraft],
  );

  const handleIndividualDiscountToggle = useCallback(
    (enabled: boolean) => {
      updatePricingDraft({ allowIndividualDiscounts: enabled });
    },
    [updatePricingDraft],
  );

  const handleDiscountTypeChange = useCallback(
    (type: 'PERCENTAGE' | 'AMOUNT') => {
      updatePricingDraft({ discountType: type, discountValue: 0 });
    },
    [updatePricingDraft],
  );

  const handleDiscountValueChange = useCallback(
    (value: number) => {
      const productsForClamp =
        flowPhase === 1 ? projectedAdjustedProducts : exchangeData.newProducts;
      let validatedValue = clampUniformDiscountValue(
        pricingDraft.discountType,
        value,
        productsForClamp.map((product) =>
          'adjustedPrice' in product
            ? { adjustedPrice: product.adjustedPrice }
            : { adjustedPrice: (product as SelectedProduct).adjustedPrice },
        ),
      );

      if (flowPhase !== 1) {
        validatedValue = clampExchangeUniformDiscountValue(
          pricingDraft.discountType,
          validatedValue,
          exchangeData.newProducts,
          exchangeCredit,
        );
      } else if (exchangeCredit > 0) {
        if (pricingDraft.discountType === 'PERCENTAGE') {
          const adjustedTotal = roundMoney(
            productsForClamp.reduce(
              (sum, product) =>
                sum +
                roundMoney(
                  'adjustedPrice' in product
                    ? product.adjustedPrice
                    : (product as SelectedProduct).adjustedPrice,
                ),
              0,
            ),
          );
          validatedValue = Math.min(
            validatedValue,
            getMaxUniformDiscountPercentageForExchange(adjustedTotal, exchangeCredit),
          );
        } else {
          validatedValue = Math.min(validatedValue, maxUniformDiscountAmount);
        }
      }

      updatePricingDraft({ discountValue: validatedValue });
    },
    [
      exchangeCredit,
      exchangeData.newProducts,
      flowPhase,
      maxUniformDiscountAmount,
      pricingDraft.discountType,
      projectedAdjustedProducts,
      updatePricingDraft,
    ],
  );

  const paymentsSectionVisible = flowPhase >= 4;
  const paymentConfigVisible = flowPhase >= 3;
  const paymentConfigEditable = flowPhase === 3 && !paymentsLocked;
  const paymentMethodsEditable = paymentsSectionVisible && !paymentsLocked;
  const paymentsEditable = paymentMethodsEditable;
  const configEditable = flowPhase === 1;
  const productsEditable = flowPhase === 2;
  const productsSectionActive = hasCommercialConfig && flowPhase >= 2;

  const committedConfigDraft = useMemo((): PricingConfigDraft => {
    if (flowPhase === 1) {
      return pricingDraft;
    }

    const hasMarkup = exchangeData.newProducts.some(
      (product) => product.markupType !== 'NONE' && product.markupValue > 0,
    );

    return {
      markupEnabled: (exchangeData.markupConfig?.enabled ?? false) || hasMarkup,
      allowIndividualMarkups: exchangeData.markupConfig?.allowIndividualMarkups ?? false,
      markupType: exchangeData.markupConfig?.orderMarkupType ?? 'AMOUNT',
      markupValue: exchangeData.markupConfig?.orderMarkupValue ?? 0,
      discountEnabled: exchangeData.discountConfig?.enabled ?? false,
      allowIndividualDiscounts: exchangeData.discountConfig?.allowIndividualDiscounts ?? false,
      discountType: exchangeData.discountConfig?.orderDiscountType ?? 'AMOUNT',
      discountValue: exchangeData.discountConfig?.orderDiscountValue ?? 0,
    };
  }, [
    flowPhase,
    pricingDraft,
    exchangeData.discountConfig,
    exchangeData.markupConfig,
    exchangeData.newProducts,
  ]);

  return {
    paymentFlowPhase: flowPhase,
    flowPhase,
    configEditable,
    productsEditable,
    productsSectionActive,
    hasCommercialConfig,
    paymentConfigVisible,
    paymentConfigEditable,
    paymentMethodsEditable,
    paymentsSectionVisible,
    paymentsEditable,
    paymentsLocked,
    commitPricingConfig,
    confirmProductsSection,
    confirmPaymentConfigSection,
    unlockConfigForEdit,
    unlockProductsForEdit,
    unlockPaymentConfigForEdit,
    unlockPaymentMethodsForEdit,
    canConfirmConfigSection,
    canConfirmProductsSection,
    hasInvalidLinePricing,
    hasInvalidProductDiscounts: hasInvalidLinePricing,
    violatesCredit: exchangeSummaryInvalid,
    summaryIncoherent,
    productMinFinalPrices,
    pricingDraft: committedConfigDraft,
    maxUniformDiscountAmount,
    orderMarkupHasNoCatalogBase,
    handleMarkupToggle,
    handleIndividualMarkupToggle,
    handleMarkupTypeChange,
    handleMarkupValueChange,
    handleDiscountToggle,
    handleIndividualDiscountToggle,
    handleDiscountTypeChange,
    handleDiscountValueChange,
  };
};
